import React from 'react';
import PropTypes from 'prop-types';
import Box from 'components/ui/Box';
import { tiers } from 'types/tiers';
import TierAvantageTitle from 'assets/images/tierAdvantage.svg';
import TierEtoileTitle from 'assets/images/tierEtoile.svg';
import TierEliteTitle from 'assets/images/tierElite.svg';
import TierCarteBlancheTitle from 'assets/images/tierCarteBlanche.svg';
import TierClassiqueTitle from 'assets/images/tierClassique.svg';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import {
  TierImage,
  TierTitleImageContainer,
  TierInfoContainer,
  TierInfoBlock,
  TierInfoItem,
  TierInfoItemLabel,
  TierInfoItemValue,
  PointsLabel
} from '../ClubEurostarMember.styles';
import { LOYALTY_TIER_1, LOYALTY_TIER_4, LOYALTY_TIER_5 } from 'constants/clubEurostar';
import config from 'utils/config';
import CompanionVouchers from './CompanionVouchers';
import TierBenefits from './TierBenefits';

const IS_TIER_BENEFIT_UPGRADE_ENABLED = config('ENABLE_TIER_BENEFIT_UPGRADE', { canOverride: true }) === 'true';

const TierInfo = ({
  tierName,
  activePoints,
  pointsToRemainInTier,
  pointsToNextTier,
  tierToDate,
  numberOfTripsToNextTier,
  customerEmail,
  customerName,
  pointsResetDate,
  customerTier,
  statusPoints,
  nextTier = false
}) => (
  <Box style={{ display: 'flex', flex: 1 }}>
    <TierInfoContainer>
      <TierImage tierName={tierName}>
        {tierName.toLowerCase() === tiers.ELITE.toLowerCase() && (
          <TierTitleImageContainer>
            <img src={TierEliteTitle} alt="tier-logo" />
          </TierTitleImageContainer>
        )}
        {tierName.toLowerCase() === tiers.ETOILE.toLowerCase() && (
          <TierTitleImageContainer>
            <img src={TierEtoileTitle} alt="tier-logo" />
          </TierTitleImageContainer>
        )}
        {tierName.toLowerCase() === tiers.AVANTAGE.toLowerCase() && (
          <TierTitleImageContainer>
            <img src={TierAvantageTitle} alt="tier-logo" />
          </TierTitleImageContainer>
        )}
        {tierName.toLowerCase() === tiers.CARTE_BLANCHE.toLowerCase() && (
          <TierTitleImageContainer>
            <img src={TierCarteBlancheTitle} alt="tier-logo" />
          </TierTitleImageContainer>
        )}
        {tierName.toLowerCase() === tiers.CLASSIQUE.toLowerCase() && (
          <TierTitleImageContainer>
            <img src={TierClassiqueTitle} alt="tier-logo" />
          </TierTitleImageContainer>
        )}
      </TierImage>
      <TierInfoBlock>
        {Number.isInteger(activePoints) && (
          <TierInfoItem>
            <TierInfoItemLabel>
              <FormattedMessage id="clubEurostar.activePoints" />
              {`: `}
            </TierInfoItemLabel>
            <TierInfoItemValue data-testid="activePoints">
              <strong>{activePoints}</strong>
              {` `}
              <PointsLabel>
                <FormattedMessage id="clubEurostar.pts" />
              </PointsLabel>
            </TierInfoItemValue>
          </TierInfoItem>
        )}
        {Number.isInteger(statusPoints) && (
          <TierInfoItem>
            <TierInfoItemLabel>
              <FormattedMessage id="clubEurostar.statusPoints" />
              {`: `}
            </TierInfoItemLabel>
            <TierInfoItemValue data-testid="statusPoints">
              <strong>{statusPoints}</strong>
              {` `}
              <PointsLabel>
                <FormattedMessage id="clubEurostar.pts" />
              </PointsLabel>
            </TierInfoItemValue>
          </TierInfoItem>
        )}
        {Number.isInteger(pointsToRemainInTier) && (
          <TierInfoItem>
            <TierInfoItemLabel>
              <FormattedMessage id="clubEurostar.pointsToRemainInTier" />
              {`: `}
            </TierInfoItemLabel>
            <TierInfoItemValue data-testid="pointsToRemainInTier">
              <strong>{pointsToRemainInTier}</strong>
              {` `}
              <PointsLabel>
                <FormattedMessage id="clubEurostar.pts" />
              </PointsLabel>
            </TierInfoItemValue>
          </TierInfoItem>
        )}
        {nextTier && tierName.toLowerCase() === tiers.ELITE.toLowerCase() ? (
          <FormattedMessage id="clubEurostar.invitationOnly" />
        ) : (
          nextTier &&
          (Number.isInteger(pointsToNextTier) ? (
            <TierInfoItem>
              <TierInfoItemLabel>
                <FormattedMessage id="clubEurostar.pointsToNextTier" />
                {`: `}
              </TierInfoItemLabel>

              <TierInfoItemValue data-testid="pointsToNextTier">
                <strong>{pointsToNextTier}</strong>
                {` `}
                <PointsLabel>
                  <FormattedMessage id="clubEurostar.pts" />
                </PointsLabel>
              </TierInfoItemValue>
            </TierInfoItem>
          ) : (
            <FormattedMessage id="clubEurostar.noPointsData" />
          ))
        )}
        {Number.isInteger(numberOfTripsToNextTier) && (
          <TierInfoItem>
            <TierInfoItemLabel>
              <FormattedMessage id="clubEurostar.tripsToNextTier" />
              {`: `}
            </TierInfoItemLabel>
            <TierInfoItemValue data-testid="tripsToNextTier">
              <strong>{numberOfTripsToNextTier}</strong>
            </TierInfoItemValue>
          </TierInfoItem>
        )}
        {tierToDate && (
          <TierInfoItem>
            <TierInfoItemLabel>
              <FormattedMessage id="clubEurostar.tierExpiryDate" />
              {`: `}
            </TierInfoItemLabel>
            <TierInfoItemValue data-testid="tierExpiryDate">
              <strong>{moment(tierToDate).format('Do MMMM YYYY')}</strong>
            </TierInfoItemValue>
          </TierInfoItem>
        )}
        {customerTier === LOYALTY_TIER_4 || customerTier === LOYALTY_TIER_5 ? (
          <CompanionVouchers customerEmail={customerEmail} customerName={customerName} pointsResetDate={pointsResetDate} />
        ) : null}
        {IS_TIER_BENEFIT_UPGRADE_ENABLED && !!customerTier && !!pointsResetDate && customerTier !== LOYALTY_TIER_1 ? (
          <TierBenefits pointsResetDate={pointsResetDate} />
        ) : null}
      </TierInfoBlock>
    </TierInfoContainer>
  </Box>
);

TierInfo.propTypes = {
  tierName: PropTypes.string,
  statusPoints: PropTypes.number,
  activePoints: PropTypes.number,
  pointsToRemainInTier: PropTypes.number,
  pointsToNextTier: PropTypes.number,
  numberOfTripsToNextTier: PropTypes.number,
  tierToDate: PropTypes.string,
  customerEmail: PropTypes.string,
  customerName: PropTypes.string,
  pointsResetDate: PropTypes.string,
  customerTier: PropTypes.string,
  nextTier: PropTypes.bool
};

export default TierInfo;
